/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-10-12 10:04:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';

// import { SubmissionError } from 'redux-form';
// import { checkHasSpecialCharacters, getShop, checkHasSpecialCharactersProduct } from 'src/helpers';
import {
  RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES,
  RECOMMENDATION_WIDGET_TRENDING_PRODUCTS_BASED_ON_TYPES,
  RECOMMENDATION_WIDGET_TRENDING_PRODUCTS_DATE_RANGE,
  RECOMMENDATION_WIDGET_RULE_BASED_RECOMMENDATION_TYPES_DEFAULT,
  AIML_MODEL_STATUS_BANNER_CONTENT
} from 'src/constants/recommendation';
import type { RecommendationWidgetType } from 'src/models/recommendation';

/**
 * Build initial recommendation widget
 *
 * @param  {RecommendationWidgetType} recommendation widget
 * @return {object}
 */
export const buildInitialWidgetValues = (recommendationWidget: RecommendationWidgetType) => {
  // Update widget field if needed
  let { htmlPlaceholder = '', id = '', recommendationType = '' } = recommendationWidget;
  let extraFields = {};
  if (_.isEmpty(htmlPlaceholder)) {
    htmlPlaceholder = `<div id='boost-sd-widget-${id}' class='boost-sd-${recommendationType}'></div>`;
  }
  switch (recommendationType) {
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['trending-products'].value: {
      // Init trending product calculation based on, = view-event if undefined
      extraFields.calculatedBasedOn = [
        _.get(
          recommendationWidget,
          'calculatedBasedOn',
          RECOMMENDATION_WIDGET_TRENDING_PRODUCTS_BASED_ON_TYPES[0].value
        )
      ];

      // Init trending product range time , = 7 if undefined
      extraFields.rangeOfTime = _.get(
        recommendationWidget,
        'rangeOfTime',
        RECOMMENDATION_WIDGET_TRENDING_PRODUCTS_DATE_RANGE[0].value
      );
      break;
    }
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value:
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value: {
      // Init value active AI base if new
      const activeAIBased = _.get(recommendationWidget, 'activeAIBased', false);
      const activeRuleBased = _.get(recommendationWidget, 'activeRuleBased', false);
      if (!activeAIBased && !activeRuleBased) {
        extraFields.activeAIBased = true;
        extraFields.activeRuleBased = false;
      }
      // Init rule based recommendation
      const ruleBasedList = _.get(
        recommendationWidget,
        'ruleBased',
        RECOMMENDATION_WIDGET_RULE_BASED_RECOMMENDATION_TYPES_DEFAULT
      );
      const ruleBasedListFormat = _.map(ruleBasedList, (rule, index) => ({
        ...rule,
        id: `rule_` + index
      }));
      extraFields.ruleBasedList = ruleBasedListFormat;
      break;
    }
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['hand-picked-products'].value: {
      const primaryRules = _.get(recommendationWidget, 'primaryRules', []);
      const rules = _.get(recommendationWidget, 'rules', [
        {
          conditions: [{ option: 'productId', value: [] }],
          consequences: [{ option: 'productId', value: [] }]
        }
      ]);
      extraFields.rules = !_.isEmpty(primaryRules) ? primaryRules : rules;
      break;
    }
    default:
  }

  return { ...recommendationWidget, ...extraFields, htmlPlaceholder };
};

/**
 * Validate general settings of widget rule
 *
 * @param  {RecommendationWidgetType} recommendation widget
 * @return {boolean}
 */
export const checkDisableNextStepFromGeneral = data => {
  const { aimlPermission, formValues } = data;
  const {
    recommendationType,
    calculatedBasedOn,
    rangeOfTime,
    activeRuleBased,
    activeAIBased,
    rules
  } = formValues;

  // if form value is not change
  // if (widgetStep === -1 && !generalFormDirty) return true;

  // Extra fields on trending product
  if (
    recommendationType === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['trending-products'].value
  ) {
    if (_.isEmpty(calculatedBasedOn) || _.isEmpty(rangeOfTime)) {
      return true;
    }
  }

  // AIML for frequent bought together, related item and recently purchase
  if (
    recommendationType ===
      RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value ||
    recommendationType === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value ||
    recommendationType === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['recently-purchased'].value
  ) {
    if (!aimlPermission) return true;
  }

  if (
    recommendationType ===
      RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value ||
    recommendationType === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value
  ) {
    // Extra fields on related product and frequent bought together

    if (!activeRuleBased && !activeAIBased) return true;

    const ruleBasedList = _.get(formValues, 'ruleBasedList', false);

    if (activeRuleBased) {
      // Check if at least 1 rule is enable
      let index = _.findIndex(ruleBasedList, rule => rule.status);
      if (index === -1) {
        return true;
      }

      // Check if metafield is empty
      index = _.findIndex(
        ruleBasedList,
        rule => rule.type.includes('sameMetafield') && rule.status && _.isEmpty(rule.metafield)
      );
      if (index !== -1) {
        return true;
      }
    }
  }

  if (
    recommendationType === RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['hand-picked-products'].value
  ) {
    if (rules) {
      const index = _.findIndex(
        rules,
        rule => _.isEmpty(rule.conditions[0].value) || _.isEmpty(rule.consequences[0].value)
      );
      if (index !== -1) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Build recommendation widget
 *
 * @param  {RecommendationWidgetType} recommendation widget
 * @return {object}
 */
export const buildWidgetValues = (data: RecommendationWidgetType) => {
  // Update widget field if needed
  let {
    recommendationType = '',
    htmlPlaceholder,
    calculatedBasedOn = [],
    activeAIBased,
    AIBasedRecommendProduct,
    secondaryAlgorithm,
    activeRuleBased,
    ruleBasedList = {},
    rules = [],
    step = 0
  } = data;
  delete data.widgetDesignSettings;
  let extraFields = {
    htmlPlaceholder: encodeURIComponent(htmlPlaceholder),
    step: step === 0 ? 1 : step
  };

  if (!activeAIBased && !activeRuleBased) {
    data.activeAIBased = true;
    data.activeRuleBased = false;
  }

  data.secondaryAlgorithm = secondaryAlgorithm?.value;
  data.AIBasedRecommendProduct = AIBasedRecommendProduct?.value;

  switch (recommendationType) {
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['trending-products'].value: {
      // Build trending product calculation based on, changing from array to string
      extraFields.calculatedBasedOn = calculatedBasedOn[0];
      break;
    }
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['related-items'].value:
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['frequently-bought-together'].value: {
      if (data.activeRuleBased) {
        // Remove id in rule base list
        const ruleBasedListFormat = _.map(ruleBasedList, (rule, index) => ({
          ...rule,
          id: undefined
        }));

        extraFields.ruleBased = ruleBasedListFormat;
      }
      extraFields.ruleBasedList = undefined;
      break;
    }
    case RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES['hand-picked-products'].value: {
      if (!_.isEmpty(rules)) {
        const formatRules = _.map(rules, item => {
          let { conditions, consequences } = item;

          conditions = _.map(conditions, condition => {
            const { option, value } = condition;
            if (option === 'productId' && !_.isEmpty(value)) {
              return { ...condition, value: _.map(value, item => parseInt(item.id)) };
            }
            return condition;
          });

          consequences = _.map(consequences, consequence => {
            const { option, value } = consequence;
            if (option === 'productId' && !_.isEmpty(value)) {
              return { ...consequence, value: _.map(value, item => parseInt(item.id)) };
            }
            return consequence;
          });
          return { conditions, consequences };
        });
        extraFields.primaryRules = rules;
        extraFields.rules = formatRules;
      }
      break;
    }
    default:
  }

  return { ...data, ...extraFields };
};

/**
 * Build initial data for default theme settings
 *
 * @param  {Object} recommendationWidget data from API
 *
 * @return initialValue
 */
export const buildInitialSettingsValues = (
  recommendationWidget: RecommendationWidgetType,
  defaultWidgetTitleSettings
) => {
  const widgetDesignSettings = _.get(recommendationWidget, 'widgetDesignSettings', {});
  const widgetName = _.get(recommendationWidget, 'widgetName', '');
  // Fonts
  const widgetTitleFont = initFonts(widgetDesignSettings, 'titleFont');
  const defaultLayoutDisplay = _.get(widgetDesignSettings, 'layoutDisplay', 'carousel');
  const maxRecommendation = defaultLayoutDisplay === 'bundle' ? 2 : 12;
  const {
    titleTextColor = '#3D4246',
    titleFont = widgetTitleFont.titleFont,
    titleFontStyle = widgetTitleFont.titleFontStyle,
    titleFontSize = 14,
    titleTextTransform = 'capitalize',
    titleAlignment = 'left',
    templateType = 'default'
  } = defaultWidgetTitleSettings;

  const defaultSetting = {
    // Widget Layout
    widgetName: widgetName || '',
    layoutDisplay: defaultLayoutDisplay,
    numberOfRecommendProduct: _.get(
      widgetDesignSettings,
      'numberOfRecommendProduct',
      maxRecommendation
    ),
    numberOfProductPerRow: _.get(widgetDesignSettings, 'numberOfProductPerRow', 4),
    themePreview: _.get(widgetDesignSettings, 'themePreview', ''),
    // Widget title
    titleTextColor: _.get(widgetDesignSettings, 'titleTextColor', titleTextColor),
    titleFont,
    titleFontStyle,
    titleFontSize: _.get(widgetDesignSettings, 'titleFontSize', titleFontSize),
    titleTextTransform: _.get(widgetDesignSettings, 'titleTextTransform', titleTextTransform),
    titleAlignment: _.get(widgetDesignSettings, 'titleAlignment', titleAlignment),
    templateType: _.get(widgetDesignSettings, 'templateType', templateType),
    isDefault: widgetDesignSettings.templateType === 'default'
  };
  if (widgetDesignSettings.bundleStyle)
    defaultSetting.bundleStyle = widgetDesignSettings.bundleStyle;
  return defaultSetting;
};

/**
 * Init fonts
 * @param {*} settings
 * @param {*} fontField
 * @returns
 */
const initFonts = (settings, fontField) => {
  const font = _.get(settings, fontField, {});
  if (_.isEmpty(font)) return { titleFont: 'Poppins', titleFontStyle: '100' };

  const { titleFont, titleFontStyle = '' } = settings;
  return {
    titleFont,
    titleFontStyle
  };
};

const buildAIMLModelKey = (modelStatus, isRelatedModels) => {
  let key = `COMPLEMENTARY_${modelStatus.Complementary}-ALTERNATIVE_${modelStatus.Alternative}-AIRELATED_${modelStatus.AIRelated}`;
  if (!isRelatedModels) {
    key = `FBT_${modelStatus.FBT}-${key}`;
  }
  return key;
};

export const getAIMLModelStatusCombine = (modelStatus: Object, isRelatedModels?: boolean) => {
  const key = buildAIMLModelKey(modelStatus, isRelatedModels);

  const modelStatusLocale = AIML_MODEL_STATUS_BANNER_CONTENT[key];
  if (!modelStatusLocale) return AIML_MODEL_STATUS_BANNER_CONTENT.PROCESSING;

  return modelStatusLocale;
};

export const buildRecommendationTaeFeatures = (
  theme: Object,
  widgetId: string,
  pageType: string
) => {
  let recommendationWidgets = {};
  recommendationWidgets[pageType] = [widgetId];
  return {
    ..._.get(theme, 'taeFeatures', {}),
    recommendation: 'installed',
    instantSearch: 'not-installed',
    recommendationWidgets
  };
};
